// src/pages/pengamatan/buah-alami/utils/pdfGenerator.js
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

/**
 * Safety check untuk memastikan dialog masih terbuka
 */
const ensureDialogStillOpen = () => {
  const dialog = document.querySelector('[role="dialog"]');
  if (!dialog) {
    throw new Error('Dialog tertutup selama proses generate PDF. Proses dibatalkan.');
  }
  return true;
};

/**
 * Fungsi utama untuk generate PDF dari form Buah Alami
 */
export const generateBuahAlamiPDF = async (selectedPengamatanData) => {
  try {
    
    if (!selectedPengamatanData) {
      throw new Error('Data pengamatan tidak tersedia');
    }

    // Safety check 1: Pastikan dialog masih terbuka
    ensureDialogStillOpen();

    // Cari elemen dengan ID pdf-content
    const element = document.getElementById('pdf-content');
    if (!element) {
      throw new Error('Element form tidak ditemukan. Pastikan form terlihat di layar.');
    }

    // Safety check 2: Pastikan dialog masih terbuka sebelum capture
    ensureDialogStillOpen();

    // Konfigurasi html2canvas dengan timeout yang lebih panjang
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: true,
      removeContainer: true,
      width: element.scrollWidth,
      height: element.scrollHeight,
      timeout: 30000, // 30 detik timeout
      onclone: (clonedDoc) => {
        // Safety check dalam cloned document
        const clonedElement = clonedDoc.getElementById('pdf-content');
        if (!clonedElement) {
          throw new Error('Elemen tidak ditemukan dalam proses cloning');
        }
        
        clonedElement.style.backgroundColor = '#ffffff';
        clonedElement.style.visibility = 'visible';
        clonedElement.style.display = 'block';
        
        // Perbaiki tampilan tabel
        const tables = clonedElement.getElementsByTagName('table');
        for (let table of tables) {
          table.style.borderCollapse = 'collapse';
          table.style.width = '100%';
          
          const cells = table.querySelectorAll('td, th');
          cells.forEach(cell => {
            cell.style.border = '1px solid #000000';
            cell.style.padding = '2px';
          });
        }
      }
    });

    // Safety check 3: Pastikan dialog masih terbuka sebelum membuat PDF
    ensureDialogStillOpen();

    // Buat PDF landscape A4
    const pdf = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    // Hitung dimensi untuk fit di halaman
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    
    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
    const width = imgWidth * ratio;
    const height = imgHeight * ratio;
    
    // Posisikan di atas halaman (Y = 0) dan tengah secara horizontal
    const x = (pdfWidth - width) / 2;
    const y = 0; // Ubah dari posisi tengah menjadi atas

    // Tambahkan background putih dan image
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, pdfWidth, pdfHeight, 'F');
    pdf.addImage(canvas, 'PNG', x, y, width, height);

    // Generate nama file
    const tanggalPengamatan = selectedPengamatanData.tanggal_pengamatan || 'unknown-date';
    const lokasi = selectedPengamatanData.lokasi || 'unknown-location';
    
    const cleanLokasi = lokasi.replace(/[^a-zA-Z0-9\s]/g, '').trim();
    const cleanTanggal = tanggalPengamatan.replace(/-/g, '');
    const fileName = `Peng. Buah Alami - ${cleanLokasi} - ${cleanTanggal}.pdf`;

    // Safety check final: Pastikan dialog masih terbuka sebelum save
    ensureDialogStillOpen();

    // Simpan PDF
    pdf.save(fileName);
    
    return fileName;

  } catch (error) {
    console.error('❌ Error generating PDF:', error);
    
    // Beri pesan error yang lebih spesifik
    if (error.message.includes('Dialog tertutup')) {
      throw new Error('Proses generate PDF dibatalkan karena dialog tertutup. Silakan coba lagi dan jangan tutup dialog selama proses.');
    }
    
    throw new Error(`Gagal generate PDF: ${error.message}`);
  }
};

/**
 * Fungsi untuk memvalidasi data sebelum generate PDF
 */
export const validateDataForPDF = (selectedPengamatanData) => {
  if (!selectedPengamatanData) {
    console.error('❌ Data pengamatan tidak tersedia');
    return false;
  }

  if (!selectedPengamatanData.lokasi) {
    console.error('❌ Data lokasi tidak tersedia');
    return false;
  }

  if (!selectedPengamatanData.tanggal_pengamatan) {
    console.error('❌ Data tanggal pengamatan tidak tersedia');
    return false;
  }

  return true;
};